import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, Typography, useTheme } from '@mui/material';
import { ScheduleOutlined } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { IDLE_REASON, lastActivity, markActivity, timeoutMs } from '../lib/idleSession';

/* Signs the person out after their chosen period of inactivity (profile drawer -> Auto sign-out), with a
   one-minute warning first. Mounted once in MainLayout. Activity is shared across tabs via localStorage. */

const WARN_MS = 60 * 1000;
const WRITE_EVERY_MS = 5 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

const fmt = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function IdleSignOut() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isDark = useTheme().palette.mode === 'dark';
  const limit = timeoutMs(user?.session_timeout_hours);

  const [remaining, setRemaining] = useState<number | null>(null); // set only while the warning shows
  const warningRef = useRef(false);
  const lastWriteRef = useRef(0);
  const signedOutRef = useRef(false);

  const signOut = useCallback(() => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    logout();
    navigate(`/login?reason=${IDLE_REASON}`, { replace: true });
  }, [logout, navigate]);

  const stay = () => {
    markActivity();
    lastWriteRef.current = Date.now();
    warningRef.current = false;
    setRemaining(null);
  };

  // Record activity (throttled). While the warning is up, only the button counts, so a stray mouse move
  // doesn't silently dismiss it.
  useEffect(() => {
    if (!user) return;
    if (lastActivity() === null) markActivity();
    const onActivity = () => {
      if (warningRef.current) return;
      const now = Date.now();
      if (now - lastWriteRef.current < WRITE_EVERY_MS) return;
      lastWriteRef.current = now;
      markActivity(now);
    };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true, capture: true }));
    return () => ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity, { capture: true }));
  }, [user]);

  // One clock for every tab. Also catches a laptop waking from sleep, since the check uses wall time.
  useEffect(() => {
    if (!user) return;
    signedOutRef.current = false;
    const tick = () => {
      const last = lastActivity() ?? Date.now();
      const left = last + limit - Date.now();
      if (left <= 0) { signOut(); return; }
      if (left <= WARN_MS) {
        warningRef.current = true;
        setRemaining(left);
      } else if (warningRef.current) {
        warningRef.current = false; // activity in another tab
        setRemaining(null);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [user, limit, signOut]);

  if (!user || remaining === null) return null;

  const ink = isDark ? '#F1F5F9' : '#0F172A';
  const muted = isDark ? '#94A3B8' : '#475569';
  return (
    <Dialog open onClose={stay} maxWidth="xs" fullWidth sx={{ zIndex: 2000 }}
      PaperProps={{ sx: { borderRadius: '14px', p: 3, bgcolor: isDark ? '#0F172A' : '#FFFFFF', backgroundImage: 'none',
        border: `1px solid ${isDark ? '#1E293B' : '#E2E8F0'}` } }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', mb: 2,
        bgcolor: isDark ? '#3A2A0A' : '#FEF3C7', color: isDark ? '#FCD34D' : '#B45309' }}>
        <ScheduleOutlined fontSize="small" />
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: ink, mb: 1 }}>Are you still there?</Typography>
      <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: muted, mb: 3 }}>
        For your security you'll be signed out in <b style={{ color: ink, fontVariantNumeric: 'tabular-nums' }}>{fmt(remaining)}</b> because
        there's been no activity.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={signOut} sx={{ textTransform: 'none', fontWeight: 600, color: muted }}>Sign out</Button>
        <Button onClick={stay} variant="contained" disableElevation autoFocus
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: '#185FA5', '&:hover': { bgcolor: '#134d86' } }}>
          Stay signed in
        </Button>
      </Box>
    </Dialog>
  );
}
